import { BindAll } from 'lodash-decorators'
import { action, computed, observable } from 'mobx'
import type { MyClassroomType, StudyReviewType, AccountGiftType } from '@xt/client/entities/my-v2/types'
import { reportEntryLog } from '@xt/client/utils/log'
import { $service } from '@xt/client/experimental/request/service'
import { CourseItem, MyCourseList } from '@xt/client/types/my-go-course'
import { EnumApiMy } from '../../api'
import { EnumProductTypeQuery } from '@xt/client/enums'
import moment from 'moment'

type MyProductStatus = {
  loading: boolean
  isError: boolean
  haveNoneCourse: boolean
}

@BindAll()
export class MyClassroomController {
  @observable userPanelData: MyClassroomType['UserPanel'] = {
    follow: 0,
    followed: 0,
    like: 0
  }
  @observable studyRecordData: MyClassroomType['StudyRecord'] = {
    learnTime: 0,
    weekLearnTime: 0,
    learnStartTime: 0,
    learnEndTime: 0,
    lastWeekLearnData: []
  }
  @observable myProductList: MyCourseList & {
    progressingResultVOS: CourseItem[]
  } = {
    courseResultVOS: [],
    activityResultVOS: [],
    // 展览习作
    showResultVOS: [],
    selfStudyResultVOS: [],
    progressingResultVOS: [],
    specialCourseResults: [],
    // 展览辅导
    remedialShowResults: []
  }
  @observable myProductStatus: MyProductStatus = {
    loading: true,
    haveNoneCourse: false,
    isError: false
  }
  @observable reviewList: Array<StudyReviewType> = []
  @observable studyActionData: MyClassroomType['StudyAction'] = {
    homework: 0,
    rewards: 0,
    daily: 0,
    collect: 0
  }
  @observable giftList: Array<AccountGiftType> = []

  get processingCourse() {
    return this.myProductList.progressingResultVOS
  }

  formatYearMonthDay(currentTime: number) {
    const [y, m, d] = moment(currentTime)
      .format('YYYY-MM-DD')
      .split('-')
      .map(v => parseInt(v))

    return {
      y,
      m,
      d
    }
  }

  /**
   * 获取我的课程列表
   */
  async onGetMyProductList() {
    // 后台直接返的课程不是按照报名时间排序的
    const [err, res] = await $service<MyCourseList>({
      url: EnumApiMy.MyCourseList
    })

    if (err) throw err

    const progressingCourse: CourseItem[] = []

    if (res.courseResultVOS) {
      res.courseResultVOS.forEach(item => {
        progressingCourse.push({ ...item, type: EnumProductTypeQuery.Course })
      })
    }
    if (res.activityResultVOS) {
      res.activityResultVOS.forEach(item => {
        progressingCourse.push({ ...item, type: EnumProductTypeQuery.DayCourse })
      })
    }
    if (res.showResultVOS) {
      res.showResultVOS.forEach(item => {
        progressingCourse.push({ ...item, type: EnumProductTypeQuery.Exhibition })
      })
    }
    if (res.selfStudyResultVOS) {
      res.selfStudyResultVOS.forEach(item => {
        progressingCourse.push({ ...item, type: EnumProductTypeQuery.SelfStudy })
      })
    }
    if (res.specialCourseResults) {
      res.specialCourseResults.forEach(item => {
        progressingCourse.push({ ...item, type: EnumProductTypeQuery.SpecialCourse })
      })
    }
    if (res.remedialShowResults) {
      res.remedialShowResults.forEach(item => {
        progressingCourse.push({ ...item, type: EnumProductTypeQuery.Exhibition })
      })
    }

    // 按报名时间排序
    return this.handleSortBySuccessTime({ ...res, progressingResultVOS: progressingCourse })
  }

  // 统一根据购买时间倒序排序
  handleSortBySuccessTime(courseList: MyCourseList & { progressingResultVOS: CourseItem[] }) {
    Object.keys(courseList).forEach(key => {
      if (!courseList[key]) return
      courseList[key].sort((a, b) => b.successTime - a.successTime)
    })
    return courseList
  }

  @action
  setGiftList(list: Array<AccountGiftType>) {
    this.giftList = [...list]
  }

  @action
  setStudyActionData(data: MyClassroomType['StudyAction']) {
    this.studyActionData = { ...data }
  }

  @action
  setReviewList(list: Array<StudyReviewType>) {
    this.reviewList = [...list]
  }

  @action
  setMyProductList(
    res: MyCourseList & {
      progressingResultVOS: CourseItem[]
    }
  ) {
    this.myProductList = {
      ...res
    }
  }

  @action
  setMyProductStatus(status: Partial<MyProductStatus>) {
    this.myProductStatus = {
      ...this.myProductStatus,
      ...status
    }
  }

  async LoadGiftList() {
    this.setGiftList([
      {
        id: 1,
        giverId: '1112029144067084319',
        giverNickname: '高山流水',
        receiveTime: 1774330844000,
        cover: 'https://testing-file.xuantong.cn/images/origin/b47b31d95ae0106a9f65ee002f975b82.jpg',
        title: '零基础书法课 入门课 | 写字旅行攻略基础书法课 入门课',
        count: 12,
        type: '1'
      }
    ])
  }

  async LoadMyProductList() {
    try {
      this.setMyProductStatus({
        loading: true
      })

      const res = await this.onGetMyProductList()

      this.setMyProductList(res)

      if (this.myProductList) {
        this.setMyProductStatus({
          haveNoneCourse: Object.keys(this.myProductList).every(key => (this.myProductList[key] ?? []).length === 0)
        })
      }
      this.setMyProductStatus({
        isError: false
      })
    } catch (error) {
      reportEntryLog('toclass.loading-data.fail', { error })
      this.setMyProductStatus({
        isError: true
      })
    } finally {
      this.setMyProductStatus({
        loading: false
      })
    }
  }

  // 获取学习互动数据
  async LoadMyStudyData() {
    this.setReviewList([
      {
        id: 1,
        type: 'homework',
        reviewed: true,
        reviewerId: '"1112029144067084319"',
        reviewerNickname: '暄桐助教',
        content: '连续练习三天，手感越来越好了。连续练习三天，手感越来越好了。连续练习三天，手感越来越好了。',
        tag: '第2课《三坟记》篆隶进阶',
        cover: 'https://testing-file.xuantong.cn/images/origin/2af4793d0f2b4e308bf0cd8dbb645ced.jpg',
        receiveTime: 1774330844000
      }
    ])
    this.setStudyActionData({
      homework: 32,
      rewards: 32,
      daily: 32,
      collect: 32
    })
  }

  @action
  async fetchUserPanelData() {
    this.userPanelData = {
      follow: 1,
      followed: 2,
      like: 3
    }
  }

  @action
  async fetchStudyRecordData() {
    this.studyRecordData = {
      learnTime: 321.4,
      weekLearnTime: 30,
      learnStartTime: 1764559349000,
      learnEndTime: 1766373749000,
      lastWeekLearnData: [
        { time: 0, date: 1766632949000 },
        { time: 10, date: 1766719349000 },
        { time: 20, date: 1766805749000 },
        { time: 35, date: 1766892149000 },
        { time: 32, date: 1766978549000 },
        { time: 26, date: 1767064949000 },
        { time: 10, date: 1767237749000 }
      ]
    }
  }

  @action
  reset() {
    this.userPanelData = {
      follow: 0,
      followed: 0,
      like: 0
    }
    this.studyRecordData = {
      learnTime: 0,
      weekLearnTime: 0,
      learnStartTime: 0,
      learnEndTime: 0,
      lastWeekLearnData: []
    }
    this.myProductList = {
      courseResultVOS: [],
      activityResultVOS: [],
      showResultVOS: [],
      selfStudyResultVOS: [],
      progressingResultVOS: [],
      specialCourseResults: [],
      remedialShowResults: []
    }
    this.myProductStatus = {
      loading: true,
      haveNoneCourse: false,
      isError: false
    }
    this.reviewList = []
    this.studyActionData = {
      homework: 0,
      rewards: 0,
      daily: 0,
      collect: 0
    }
    this.giftList = []
  }
}
